/* Bilingual (EN/AR) support. `t(en)` looks up the Arabic dictionary exported
 * from the prototype; `L(en, ar)` picks inline strings. Direction + <html lang>
 * follow the language; preference persists in localStorage (best effort). */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import AR from "../../../contracts/i18n-ar.json";
import LOOKUPS from "../../../contracts/lookups.json";
import type { Lang } from "../data/types";
import { useCountry } from "../lib/country";
import { localText } from "../lib/localText";

const DICT = AR as Record<string, string>;
const LK = LOOKUPS as unknown as { DEPT: Record<string, [string, string]>; BR: Record<string, [string, string]>; NAT: Record<string, [string, string]>; TITLE_AR: Record<string, string> };

export type Theme = "light" | "dark" | null;
const PREF_KEY = "starthr-prefs";

function loadPrefs(): { lang?: Lang; theme?: Theme } {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || "{}"); } catch { return {}; }
}
function savePrefs(p: { lang: Lang; theme: Theme }) {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

export interface I18n {
  lang: Lang;
  dir: "ltr" | "rtl";
  isAr: boolean;
  setLang(l: Lang): void;
  toggleLang(): void;
  theme: Theme;
  isDark: boolean;
  toggleTheme(): void;
  t(s: string): string;
  L(en: string, ar: string): string;
  dept(k: string): string;
  branch(k: string): string;
  nat(k: string): string;
  title(s: string): string;
  name(e: { nameEn: string; nameAr: string }): string;
  name2(e: { nameEn: string; nameAr: string }): string;
}

export function makeI18n(lang: Lang, theme: Theme = null, set: { setLang?: (l: Lang) => void; setTheme?: (t: Theme) => void } = {}): I18n {
  const isAr = lang === "ar";
  const pick = (pair: [string, string] | undefined, k: string) => (pair ? pair[isAr ? 1 : 0] : k);
  const sysDark = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDark = theme ? theme === "dark" : sysDark;
  return {
    lang, dir: isAr ? "rtl" : "ltr", isAr,
    setLang: (l) => set.setLang?.(l),
    toggleLang: () => set.setLang?.(isAr ? "en" : "ar"),
    theme, isDark,
    toggleTheme: () => set.setTheme?.(isDark ? "light" : "dark"),
    t: (s) => (isAr && DICT[s] ? DICT[s] : s),
    L: (en, ar) => (isAr ? ar : en),
    dept: (k) => pick(LK.DEPT[k], k),
    branch: (k) => pick(LK.BR[k], k),
    nat: (k) => pick(LK.NAT[k], k),
    title: (s) => (isAr && LK.TITLE_AR[s] ? LK.TITLE_AR[s] : s),
    name: (e) => (isAr ? e.nameAr : e.nameEn),
    name2: (e) => (isAr ? e.nameEn : e.nameAr),
  };
}

export const LOOKUP = LK;

const Ctx = createContext<I18n>(makeI18n("en"));

export function I18nProvider({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) {
  const saved = loadPrefs();
  const [lang, setLangS] = useState<Lang>(initialLang ?? saved.lang ?? "en");
  const [theme, setThemeS] = useState<Theme>(saved.theme ?? null);
  const setLang = useCallback((l: Lang) => setLangS(l), []);
  const setTheme = useCallback((t: Theme) => setThemeS(t), []);
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    root.setAttribute("lang", lang);
    if (theme) root.setAttribute("data-theme", theme); else root.removeAttribute("data-theme");
    savePrefs({ lang, theme });
  }, [lang, theme]);
  const value = useMemo(() => makeI18n(lang, theme, { setLang, setTheme }), [lang, theme, setLang, setTheme]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Language helpers. For a company outside Saudi Arabia (see lib/country.tsx) every t() / L() string is re-worded
 * with the country's own terms (lib/localText.ts): GOSI → GPSSA, SAR → AED, Iqama → Emirates ID… */
export function useI18n(): I18n {
  const base = useContext(Ctx);
  const c = useCountry();
  return useMemo(() => {
    if (c.code === "SA") return base;
    const loc = (s: string) => localText(s, c, base.lang);
    return { ...base, t: (s) => loc(base.t(s)), L: (en, ar) => loc(base.L(en, ar)) };
  }, [base, c]);
}
